import { ModalController } from "@theme/modal-controller";

// Shared fetch — runs once regardless of how many <localization-form> instances exist on the page
let _locationPromise = null;
function getLocationSuggestion() {
  if (!_locationPromise) {
    _locationPromise = fetch(
      window.Shopify.routes.root +
        "browsing_context_suggestions.json" +
        "?country[enabled]=true" +
        `&country[exclude]=${window.Shopify.country}` +
        "&language[enabled]=true" +
        `&language[exclude]=${window.Shopify.language}`,
    )
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }
  return _locationPromise;
}

function getBrowserCountryFallback() {
  const lang = navigator.language || "";
  const parts = lang.split("-");
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : null;
}

function extractDetectedCountry(data) {
  if (!data) return null;

  const detected = data.detected_values || {};
  const suggestion = data.suggestions?.[0]?.parts || {};

  const candidates = [
    detected.country?.handle,
    detected.country?.iso_code,
    detected.country?.code,
    typeof detected.country === "string" ? detected.country : null,
    suggestion.country?.handle,
    suggestion.country?.iso_code,
  ];

  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) return value.trim().toUpperCase();
  }
  return null;
}

// ── LocalizationForm ──────────────────────────────────────────────────────────

class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector('input[name="language_code"], input[name="country_code"]'),
      button: this.querySelector("button"),
      panel: this.querySelector("ul"),
    };
    this.elements.button.addEventListener("click", this.openSelector.bind(this));
    this.elements.button.addEventListener("focusout", this.closeSelector.bind(this));
    this.addEventListener("keyup", this.onContainerKeyUp.bind(this));
    this.querySelectorAll("a").forEach((item) =>
      item.addEventListener("click", this.onItemClick.bind(this)),
    );

    if (!sessionStorage.getItem("localization_form_submitted")) {
      this.detectLocation();
    }
  }

  hidePanel() {
    this.elements.button.setAttribute("aria-expanded", "false");
    this.elements.panel.setAttribute("hidden", true);
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;
    this.hidePanel();
    this.elements.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector("form");
    this.elements.input.value = event.currentTarget.dataset.value;
    if (form) {
      form.submit();
      sessionStorage.setItem("localization_form_submitted", "true");
    }
  }

  openSelector() {
    this.elements.button.focus();
    this.elements.panel.toggleAttribute("hidden");
    this.elements.button.setAttribute(
      "aria-expanded",
      (this.elements.button.getAttribute("aria-expanded") === "false").toString(),
    );
  }

  closeSelector(event) {
    const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === "BUTTON";
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }

  async detectLocation() {
    try {
      const data = await getLocationSuggestion();
      if (!data?.suggestions?.length) return;

      const suggestion = data.suggestions[0];
      const currentCountry =
        window.Theme?.localization?.country?.iso_code || window.Shopify.country;

      if (
        suggestion.parts.country?.handle === currentCountry ||
        suggestion.parts.country?.name === window.Theme?.localization?.country?.name
      ) {
        return;
      }

      const modal = document.querySelector('[data-modal="location-selector-id"]');
      if (!modal) {
        console.warn("Location selector modal not found in DOM");
        return;
      }

      const modalController = new ModalController();
      modalController.presentModal("location-selector-id");
    } catch (error) {
      console.error("Error fetching browsing context suggestions:", error);
    }
  }
}

// ── LocaleNotice (store switcher) ────────────────────────────────────────────

const LN_KEY = "_qf_ln";
const LN_PARAM = "_qfln";

// Known two-part ccTLDs — prevents setting a cookie on a public suffix (e.g. ".co.uk")
const MULTI_PART_TLDS = new Set([
  "co.uk", "co.nz", "co.za", "co.jp", "co.in", "co.kr",
  "com.au", "com.br", "com.mx", "com.ar", "com.sg", "com.hk",
  "net.au", "org.au", "gov.au", "edu.au", "org.uk", "me.uk", "ne.jp",
]);

function cookieParentDomain(hostname) {
  const parts = hostname.split(".");
  if (parts.length <= 2) return hostname;
  const tldTwo = parts.slice(-2).join(".");
  const regDomain = MULTI_PART_TLDS.has(tldTwo) ? parts.slice(-3).join(".") : parts.slice(-2).join(".");
  return hostname === regDomain ? hostname : "." + regDomain;
}

function setNoticeDismissed() {
  document.cookie = `${LN_KEY}=1; Domain=${cookieParentDomain(location.hostname)}; Path=/; Max-Age=2592000; SameSite=Lax`;
  try {
    localStorage.setItem(LN_KEY, "1");
  } catch (_) {}
}

function isNoticeDismissed() {
  const params = new URLSearchParams(location.search);
  if (params.get(LN_PARAM)) {
    setNoticeDismissed();
    params.delete(LN_PARAM);
    const cleaned =
      location.pathname + (params.toString() ? "?" + params.toString() : "") + location.hash;
    history.replaceState(null, "", cleaned);
    return true;
  }
  if (document.cookie.split(";").some((c) => c.trim().startsWith(LN_KEY + "="))) return true;
  try {
    return !!localStorage.getItem(LN_KEY);
  } catch (_) {
    return false;
  }
}

class LocaleNotice extends HTMLElement {
  connectedCallback() {
    this.dialog = this.closest("dialog");
    this.preview = this.hasAttribute("data-preview");
    this.mode = "switch";

    let stores = [];
    try {
      stores = JSON.parse(this.dataset.stores || "[]");
    } catch (e) {
      return;
    }

    this.bindSwitcher();
    this.bindDialog();

    if (this.preview) {
      this.render(stores[0] || { label: "Example Store", url: "#" });
      this.show("suggest");
      return;
    }

    if (isNoticeDismissed()) return;
    if (!stores.length) return;

    this.detectAndShow(stores);
  }

  // ── Manual mode: one button per other store ───────────────────────────────

  bindSwitcher() {
    this.querySelectorAll("[data-store-link]").forEach((link) => {
      // Liquid filters these out by host; this catches any the host match missed
      if (this.isCurrentOrigin(link.href)) {
        link.hidden = true;
        return;
      }
      const dest = this.destination(link.href);
      if (dest) link.href = dest;
    });
  }

  isCurrentOrigin(url) {
    try {
      return new URL(url, location.origin).origin === location.origin;
    } catch (_) {
      return false;
    }
  }

  // Carries any discount code over and flags the destination so it doesn't
  // immediately suggest switching back
  destination(url) {
    try {
      const dest = new URL(url, location.origin);
      const discount = new URLSearchParams(location.search).get("discount");
      if (discount && !dest.searchParams.has("discount")) dest.searchParams.set("discount", discount);
      dest.searchParams.set(LN_PARAM, "1");
      return dest.toString();
    } catch (_) {
      return null;
    }
  }

  // ── Suggestion mode ───────────────────────────────────────────────────────

  render(match) {
    const message = this.querySelector("#localeMessage");
    const link = this.querySelector("#localeLink");

    if (message) {
      message.textContent = (message.dataset.template || "").replace("##", match.label);
    }
    if (link) {
      link.href = this.destination(match.url) || match.url;
    }
  }

  // ── Shared dialog plumbing ────────────────────────────────────────────────

  bindDialog() {
    const dismiss = this.querySelector("#localeDismiss");
    if (dismiss) dismiss.addEventListener("click", () => this.dismiss());

    if (!this.dialog) return;

    // The header globe uses command="show-modal"; reset to the manual pane so a
    // spent suggestion isn't what the merchant's customer sees
    this.dialog.addEventListener("command", (event) => {
      if (event.command === "show-modal") this.setMode("switch");
    });

    // Only a dismissed *suggestion* should be remembered — closing the manual
    // switcher must not suppress future suggestions
    this.dialog.addEventListener("close", () => {
      if (this.mode === "suggest" && !this.preview) setNoticeDismissed();
    });

    this.dialog.addEventListener("click", (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    // Fallback for browsers without the command/commandfor invoker API
    if (!("commandForElement" in HTMLButtonElement.prototype) && this.dialog.id) {
      document
        .querySelectorAll(`[commandfor="${CSS.escape(this.dialog.id)}"]`)
        .forEach((trigger) => {
          const command = trigger.getAttribute("command");
          trigger.addEventListener("click", () => {
            if (command === "close") this.dialog.close();
            else this.show("switch");
          });
        });
    }
  }

  setMode(mode) {
    this.mode = mode;
    if (!this.dialog) return;
    this.dialog.dataset.mode = mode;
    this.dialog.setAttribute(
      "aria-labelledby",
      mode === "suggest" ? "localeMessage" : "storeSwitcherTitle",
    );
  }

  show(mode) {
    this.setMode(mode);

    if (!this.dialog) {
      // No dialog wrapper — fall back to revealing in place
      this.removeAttribute("hidden");
      return;
    }

    if (!this.dialog.open) this.dialog.showModal();
  }

  dismiss() {
    if (this.dialog) {
      this.dialog.close();
      return;
    }
    setNoticeDismissed();
    this.hidden = true;
  }

  async detectAndShow(stores) {
    try {
      const data = await getLocationSuggestion();

      const detectedCountry = extractDetectedCountry(data) || getBrowserCountryFallback();
      if (!detectedCountry) return;

      const match = stores.find(
        (s) =>
          Array.isArray(s.countries) &&
          s.countries.map((c) => c.toUpperCase()).includes(detectedCountry),
      );
      if (!match) return;

      // Nothing to suggest if the match is the store we're already on
      if (this.isCurrentOrigin(match.url)) return;

      this.render(match);
      this.show("suggest");
    } catch (e) {
      // Enhancement only — fail silently
    }
  }
}

if (!customElements.get("localization-form")) {
  customElements.define("localization-form", LocalizationForm);
}
if (!customElements.get("locale-notice")) {
  customElements.define("locale-notice", LocaleNotice);
}